import { extractMeetingSlug } from '@/container/demo-flow/meeting-booker/meeting-booker-utils';
import { useMutateBookMeeting } from '@/hooks/mutation/useMutateBookMeeting';
import { useMutateSubmitSalesBookingForm } from '@/hooks/mutation/useMutateSubmitSalesBookingForm';
import { useQueryMeetingAvailability } from '@/hooks/query/useQueryMeetingAvailability';
import { isOnPrem } from '@/utils/buildProfile';
import { getHubSpotTrackingCookie } from '@/utils/hubspot/getHubSpotTrackingCookie';
import type {
  Heidi2BookDemoContact,
  Heidi2BookDemoPort,
  Heidi2BookMeetingPort,
} from '@heidi/agents';
import { type SalesBookingFormName } from '@heidi/schema/ml-scribe';
import { useCurrentUserQueryResult, useCurrentWorkspace } from '@heidi/workspace/data';
import { useMemo } from 'react';

import { canOfferHeidi2OnboardingBookDemo } from './heidi-2-book-demo-eligibility';
import { Heidi2BookDemoPhoneInput } from './heidi-2-book-demo-phone-input';

const HEIDI_2_ONBOARDING_DEMO_FORM_NAME =
  'heidi_2_onboarding_demo' as SalesBookingFormName;

const HEIDI_2_BOOK_DEMO_PAGE_NAME = 'In-product heidi ii contact sales';

const toSalesBookingFields = (contact: Heidi2BookDemoContact) => {
  const fields = [
    { name: 'firstname', value: contact.firstName },
    { name: 'lastname', value: contact.lastName },
    { name: 'email', value: contact.email },
    { name: 'company', value: contact.organisation },
    { name: 'contact_country', value: contact.country },
  ];
  const phone = contact.phone.trim();
  if (phone) {
    fields.push({ name: 'phone', value: phone });
  }
  return fields;
};

/**
 * Reads and books the HubSpot meeting link behind a scheduler URL.
 *
 * `useAvailability` delegates to the shared query hook, so paging months in
 * the dialog hits the same cache the onboarding availability probe warmed.
 * The dialog calls it unconditionally in one place, which is what keeps a
 * hook-shaped port legal.
 */
const useHeidi2BookMeetingPort = (): Heidi2BookMeetingPort => {
  const { mutateAsync: bookMeeting } = useMutateBookMeeting();

  return useMemo(
    () => ({
      useAvailability: (meetingUrl, monthOffset, timezone) => {
        const { data, isLoading, isError } = useQueryMeetingAvailability(
          extractMeetingSlug(meetingUrl),
          monthOffset,
          timezone
        );
        return {
          availability: data?.success ? data.data : undefined,
          isLoading,
          // A 200 carrying `success: false` is HubSpot refusing the link, not
          // an empty month — surface it as an error rather than "no times".
          isError: isError || data?.success === false,
        };
      },
      book: async (booking) => {
        const slug = extractMeetingSlug(booking.meetingUrl);
        if (!slug) throw new Error('Meeting URL has no slug');
        const phone = booking.phone?.trim();
        await bookMeeting({
          slug,
          duration: booking.durationMs,
          startTime: booking.startMillisUtc,
          firstName: booking.firstName,
          lastName: booking.lastName,
          email: booking.email,
          timezone: booking.timezone,
          ...(phone ? { formFields: { phone } } : {}),
        });
      },
    }),
    [bookMeeting]
  );
};

/**
 * The Heidi II book-a-demo port: prefilled from the signed-in user, submitted
 * as a Heidi 2 onboarding demo lead through sales-booking. Shared by every
 * surface that offers the form, so they all file the same kind of lead.
 *
 * Returns nothing unless the clinician is a workspace admin — members must
 * not be offered a 1:1.
 *
 * `meeting` is withheld on-prem, where there is no path out to HubSpot — the
 * dialog then offers the lead form in every country.
 */
export const useHeidi2BookDemoPort = (
  onSubmitted?: (contact: Heidi2BookDemoContact) => void
): Heidi2BookDemoPort | undefined => {
  const currentWorkspace = useCurrentWorkspace();
  const { data: contact } = useCurrentUserQueryResult<Heidi2BookDemoContact>((user) => ({
    firstName: user.first_name ?? '',
    lastName: user.last_name ?? '',
    email: user.email ?? '',
    organisation: user.organisation_info?.name || user.practice_name || '',
    country: user.country ?? '',
    phone: user.phone_number ?? '',
  }));
  const { mutateAsync: submitSalesBookingForm } = useMutateSubmitSalesBookingForm();
  const meeting = useHeidi2BookMeetingPort();

  return useMemo(() => {
    if (!canOfferHeidi2OnboardingBookDemo(currentWorkspace?.user_role)) {
      return undefined;
    }
    return {
      contact,
      PhoneInput: Heidi2BookDemoPhoneInput,
      isOnPrem,
      meeting: isOnPrem ? undefined : meeting,
      submit: async (submitted: Heidi2BookDemoContact) => {
        await submitSalesBookingForm({
          formName: HEIDI_2_ONBOARDING_DEMO_FORM_NAME,
          body: {
            fields: toSalesBookingFields(submitted),
            context: {
              page_name: HEIDI_2_BOOK_DEMO_PAGE_NAME,
              page_uri: typeof window !== 'undefined' ? window.location.href : null,
              hutk: getHubSpotTrackingCookie() ?? null,
            },
          },
        });
        onSubmitted?.(submitted);
      },
    };
  }, [
    contact,
    currentWorkspace?.user_role,
    meeting,
    onSubmitted,
    submitSalesBookingForm,
  ]);
};
